import { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CheckmarkOutline } from '@carbon/icons-react';

import {
  BaseLayout,
  SurveyThankYou,
  SkeletonLoader,
  Spinner,
} from '@/components';
import { Button, Card, CardContent, Label, Textarea } from '@/components/ui';
import {
  getOptionClassName,
  flattenQuestions,
  transformAnswersToSubmission,
} from '../../../utils/route-survey';
import {
  SURVEY_POSITIVE_SCORE_THRESHOLD,
  ENPS_QUESTION,
} from '../../../constants/survey.constants';
import type {
  SurveyOption,
  RouteSurveyConfig,
} from '../../../types/survey-config.types';
import type { Answer } from '../../../types/route-survey.types';
import { getSurveyConfig } from '../../../api/survey-config.api';
import { createRouteSurvey } from '../../../api/route-survey.api';
import { useToast } from '../../../contexts/toast-context';
import { TOAST_TYPES } from '../../../constants/common.constants';

import './route-surveys-add-style.scss';

export const Route = createFileRoute('/route-surveys/add/')({
  component: RouteSurveyCreate,
});

function RouteSurveyCreate() {
  const { addToast } = useToast();
  const [config, setConfig] = useState<RouteSurveyConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<number, Answer>>(new Map());
  const [comment, setComment] = useState('');
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideNextButton, setHideNextButton] = useState<boolean>(false);

  // Fetch survey config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoading(true);
        const response = await getSurveyConfig();
        setConfig(response.routeSurveyConfig);
      } catch (err) {
        addToast(
          err instanceof Error ? err.message : 'Failed to load survey config',
          { type: TOAST_TYPES.ERROR },
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Flatten all questions from categories
  const allQuestions = config ? flattenQuestions(config) : [];
  const totalQuestions = allQuestions.length;
  const currentItem = allQuestions[currentQuestionIndex];
  const currentQuestion = currentItem?.question;
  const currentCategory = currentItem?.category;
  const currentAnswer = currentQuestion
    ? answers.get(currentQuestion.id)
    : undefined;
  const isEnpsQuestion = currentQuestion?.id === ENPS_QUESTION.id;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  // Save comment to answer
  const handleCommentChange = (value: string) => {
    setComment(value);

    // Save comment to current answer immediately
    const currentAnswerData = answers.get(currentQuestion.id);
    if (currentAnswerData) {
      const newAnswers = new Map(answers);
      newAnswers.set(currentQuestion.id, {
        ...currentAnswerData,
        comment: value || undefined, // Save empty string as undefined
      });
      setAnswers(newAnswers);
    }
  };

  // Handle next button and comments when question index changes
  useEffect(() => {
    // Hide Next button by default for new unanswered questions
    // Show Next button if question was already answered (user navigated back)
    setHideNextButton(!currentAnswer);

    // Load previous answer's comment if exists and determine if comment box should show
    if (currentAnswer?.comment) {
      setComment(currentAnswer.comment);
      setShowCommentBox(true);
    } else if (
      currentAnswer &&
      currentAnswer.score <= SURVEY_POSITIVE_SCORE_THRESHOLD
    ) {
      // Show comment box for negative responses
      setComment('');
      setShowCommentBox(true);
    } else if (isEnpsQuestion && currentAnswer) {
      // Always show comment box for eNPS if answered
      setComment('');
      setShowCommentBox(true);
    } else {
      setComment('');
      setShowCommentBox(false);
    }
  }, [currentQuestionIndex, currentAnswer, isEnpsQuestion]);

  const handleOptionClick = (option: SurveyOption) => {
    const currentAnswer = answers.get(currentQuestion.id);

    const isSameOption = currentAnswer?.optionId === option.id;
    const isNegativeOption = option.score <= SURVEY_POSITIVE_SCORE_THRESHOLD;

    // Preserve comment if re-clicking same option, selecting a negative option, or for eNPS questions
    const preservedComment =
      isSameOption || isNegativeOption || isEnpsQuestion
        ? currentAnswer?.comment
        : undefined;

    const newAnswer: Answer = {
      questionId: currentQuestion.id,
      optionId: option.id,
      score: option.score,
      ...(preservedComment && { comment: preservedComment }),
    };

    const newAnswers = new Map(answers);
    newAnswers.set(currentQuestion.id, newAnswer);
    setAnswers(newAnswers);

    // eNPS: always show comment, never auto-advance
    if (isEnpsQuestion) {
      setShowCommentBox(true);
      setComment(preservedComment ?? '');
      return;
    }

    // POSITIVE OPTION
    if (option.score > SURVEY_POSITIVE_SCORE_THRESHOLD) {
      setShowCommentBox(false);
      setComment(''); // always clear comment on positive

      const wasNegativeBefore =
        !currentAnswer ||
        currentAnswer.score <= SURVEY_POSITIVE_SCORE_THRESHOLD;

      // Auto-advance only when newly positive
      if (wasNegativeBefore && currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }

      return;
    }

    // NEGATIVE OPTION
    setShowCommentBox(true);
    setHideNextButton(false); // Show Next button for negative answers
    setComment(preservedComment ?? '');
  };

  // Handle next button click (advance to next question)
  const handleNext = () => {
    setComment('');
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  // Handle previous button click (go back to previous question)
  const handlePrevious = () => {
    setComment('');
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  // Handle submit button click (transform answers to nested structure for backend submission)
  const handleSubmit = async () => {
    if (!config) return;

    try {
      setIsSubmitting(true);
      const data = transformAnswersToSubmission(answers, config);
      const response = await createRouteSurvey(data);

      addToast(response.message || 'Survey submitted successfully', {
        type: TOAST_TYPES.SUCCESS,
      });

      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to submit survey', {
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAnswered = currentAnswer !== undefined;
  // Show Next button if the question is answered and not the last question and not hiding the next button
  const showNextButton = isAnswered && !isLastQuestion && !hideNextButton;
  // Show Finish button if the last question is answered
  const showFinishButton = isAnswered && isLastQuestion;

  // Show thank you page after submission
  if (isSubmitted) {
    return (
      <BaseLayout
        header={{
          headerText: 'Route Survey',
        }}
      >
        <SurveyThankYou message="Your route survey has been submitted successfully. We appreciate your input." />
      </BaseLayout>
    );
  }

  return (
    <BaseLayout
      header={{
        headerText: 'Route Survey',
      }}
    >
      <div className="route-survey-add-container">
        {/* Progress Bar Section */}
        {isLoading ? (
          <div className="route-survey-add-progress">
            <SkeletonLoader count={1} height={30} />
          </div>
        ) : (
          config &&
          currentQuestion && (
            <div className="route-survey-add-progress">
              <div className="route-survey-add-progress-text">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </div>
              <div className="route-survey-add-progress-bar">
                <div
                  className="route-survey-add-progress-fill"
                  style={{
                    width: `${
                      ((currentQuestionIndex + 1) / totalQuestions) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
          )
        )}

        {/* Survey Form Section */}
        <Card className="route-survey-add-card">
          <CardContent>
            {isLoading ? (
              <SkeletonLoader count={10} height={30} />
            ) : !config ? (
              <div>No survey configuration available</div>
            ) : (
              <>
                {/* Category Header */}
                <div className="route-survey-add-category">
                  {currentCategory.name.toUpperCase()}
                </div>

                {/* Question Text */}
                <h2 className="route-survey-add-question">
                  {currentQuestion.text}
                </h2>

                {/* Options Section */}
                <div
                  className={`route-survey-add-options ${
                    isEnpsQuestion ? 'route-survey-add-options-enps' : ''
                  }`}
                >
                  {currentQuestion.options.map((option) => {
                    const optionClass = getOptionClassName(
                      option.score,
                      isEnpsQuestion,
                    );

                    const isSelected = currentAnswer?.optionId === option.id;

                    return (
                      <Button
                        key={option.id}
                        className={`route-survey-add-option ${optionClass} ${
                          isSelected ? 'route-survey-add-option-selected' : ''
                        } ${
                          isEnpsQuestion ? 'route-survey-add-option-enps' : ''
                        }`}
                        onClick={() => handleOptionClick(option)}
                      >
                        {option.label}
                        {!isEnpsQuestion && isSelected && (
                          <CheckmarkOutline className="route-survey-add-option-check medium-icon" />
                        )}
                      </Button>
                    );
                  })}
                </div>

                {/* Comment Box Section */}
                {showCommentBox && (
                  <div className="route-survey-add-comment">
                    <Label>
                      {isEnpsQuestion
                        ? 'Please share any additional feedback (optional)'
                        : 'Can you tell me more about that?'}
                    </Label>
                    <Textarea
                      value={comment}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      placeholder="Enter your comment..."
                      rows={3}
                    />
                  </div>
                )}

                {/* NavigationButtons Section */}
                <div className="route-survey-add-actions">
                  {currentQuestionIndex > 0 && (
                    <Button
                      variant="basic-secondary"
                      size="medium"
                      className="route-survey-add-previous-button"
                      onClick={handlePrevious}
                    >
                      Previous
                    </Button>
                  )}
                  {showNextButton && (
                    <Button
                      variant="basic-primary"
                      size="medium"
                      className="route-survey-add-next-button"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  )}
                  {showFinishButton && (
                    <Button
                      variant="basic-primary"
                      size="medium"
                      className="route-survey-add-next-button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting && <Spinner size="small" />}
                      <span>Finish</span>
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </BaseLayout>
  );
}
